import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { LogIn, LogOut, LayoutGrid, Home, Users, Palette } from "lucide-react";

export default function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user && (user.role === "admin" || user.role === "super_admin");
  const isCreator = user && user.role === "creator";

  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 bg-black/60 backdrop-blur-xl border-b border-white/10"
      data-testid="site-header"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
        <Link to="/" className="group flex items-center gap-3" data-testid="logo-link">
          <span className="w-2.5 h-2.5 bg-glow-primary rounded-full shadow-[0_0_12px_rgba(0,240,255,0.8)] group-hover:scale-125 transition-transform duration-300" />
          <span className="font-heading font-900 text-lg tracking-[0.2em] text-white">
            G.L.O.W
          </span>
        </Link>

        <div className="flex items-center gap-3">
          <Link to="/">
            <Button
              variant="ghost"
              className="text-white/70 hover:text-glow-primary hover:bg-glow-primary/10 rounded-none font-mono text-xs uppercase tracking-wider"
              data-testid="nav-gallery-btn"
            >
              <Home className="w-4 h-4 mr-2" /> Gallery
            </Button>
          </Link>

          <Link to="/creators">
            <Button
              variant="ghost"
              className="text-white/70 hover:text-glow-accent hover:bg-glow-accent/10 rounded-none font-mono text-xs uppercase tracking-wider"
              data-testid="nav-creators-btn"
            >
              <Users className="w-4 h-4 mr-2" /> Creators
            </Button>
          </Link>

          {isCreator && (
            <Link to="/studio">
              <Button
                variant="ghost"
                className="text-white/70 hover:text-glow-accent hover:bg-glow-accent/10 rounded-none font-mono text-xs uppercase tracking-wider"
                data-testid="nav-studio-btn"
              >
                <Palette className="w-4 h-4 mr-2" /> My Gallery
              </Button>
            </Link>
          )}

          {user ? (
            <>
              {isAdmin && (
                <Link to="/admin">
                  <Button
                    variant="ghost"
                    className="text-white/70 hover:text-glow-primary hover:bg-glow-primary/10 rounded-none font-mono text-xs uppercase tracking-wider"
                    data-testid="nav-admin-btn"
                  >
                    <LayoutGrid className="w-4 h-4 mr-2" /> Admin
                  </Button>
                </Link>
              )}
              {user.role === "super_admin" && (
                <span
                  className="hidden sm:inline font-mono text-[10px] uppercase tracking-wider text-glow-accent border border-glow-accent/40 px-2 py-1"
                  data-testid="super-admin-badge"
                >
                  Super Admin
                </span>
              )}
              {user.picture && (
                <img
                  src={user.picture}
                  alt={user.name}
                  className="w-8 h-8 rounded-full border border-glow-primary/50"
                  data-testid="user-avatar"
                />
              )}
              <Button
                onClick={handleLogout}
                variant="outline"
                className="border-glow-secondary/50 text-glow-secondary hover:bg-glow-secondary/10 rounded-none font-mono text-xs uppercase tracking-wider"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" /> Exit
              </Button>
            </>
          ) : (
            <Button
              onClick={handleLogin}
              className="bg-glow-primary text-black font-bold hover:bg-glow-primary/80 rounded-none font-mono text-xs uppercase tracking-wider shadow-[0_0_20px_rgba(0,240,255,0.3)]"
              data-testid="admin-login-btn"
            >
              <LogIn className="w-4 h-4 mr-2" /> Admin Login
            </Button>
          )}
        </div>
      </div>
    </header>
  );
}
